/**
 * SR integration error codes for user-facing diagnostics.
 * Users can report the code; engineers map it to the failure in logs.
 *
 * Code reference (internal — not shown to users):
 * SR-001  Connection status — network/unknown
 * SR-002  Connection status — 401
 * SR-003  Connection status — 403
 * SR-004  Connection status — 5xx
 * SR-005  OAuth start — network/unknown
 * SR-006  OAuth start — 403
 * SR-007  OAuth callback — user denied
 * SR-008  OAuth callback — missing params
 * SR-009  OAuth callback — invalid state
 * SR-010  OAuth callback — connection failed
 * SR-011  Disconnect — failed
 * SR-012  Hub login — not connected
 * SR-013  Hub login — session expired
 * SR-014  Hub login — SR unavailable
 * SR-015  Hub login — unknown
 * SR-016  OAuth callback — integration not configured
 * SR-017  OAuth callback — token exchange failed
 * SR-018  OAuth callback — subscription failed
 * SR-019  OAuth callback — save connection failed
 * SR-020  SR referrals list — network/unknown
 * SR-021  SR referrals list — 403
 * SR-022  SR referrals list — 5xx
 * SR-030  SR queue summary — network/unknown
 * SR-031  SR queue summary — 403
 * SR-032  SR queue summary — 5xx
 * SR-050  SR ingest failures list — network/unknown
 * SR-051  SR ingest failures list — 403
 * SR-052  SR ingest failures list — 5xx
 * SR-053  SR manual triage queue — failed
 * SR-054  SR ingest retry — failed
 * SR-040  SR message send — failed
 * SR-041  SR outbound retry — failed
 */

export class SRApiError extends Error {
  readonly code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = 'SRApiError';
    this.code = code;
  }
}

export interface SRErrorDisplay {
  code: string;
  message: string;
}

export const SR_OAUTH_CALLBACK_ERRORS: Record<string, SRErrorDisplay> = {
  oauth_denied: {
    code: 'SR-007',
    message: 'SR connection was cancelled.',
  },
  invalid_request: {
    code: 'SR-023',
    message:
      'SR rejected the OAuth callback (invalid_request). The staging redirect URI may not be registered with Cervin — contact support.',
  },
  unauthorized_client: {
    code: 'SR-024',
    message: 'SR rejected the OAuth client credentials. Verify staging SR client ID/secret with Cervin.',
  },
  missing_params: {
    code: 'SR-008',
    message: 'SR connection was incomplete. Please try again.',
  },
  invalid_state: {
    code: 'SR-009',
    message: 'SR connection session expired. Please try again.',
  },
  connection_failed: {
    code: 'SR-010',
    message: 'SR connection could not be completed. Please try again.',
  },
  config_error: {
    code: 'SR-016',
    message: 'SR integration is not configured correctly. Contact support.',
  },
  exchange_failed: {
    code: 'SR-017',
    message: 'SR authorization could not be completed. Please try again.',
  },
  subscribe_failed: {
    code: 'SR-018',
    message: 'SR subscription could not be activated. Please try again.',
  },
  save_failed: {
    code: 'SR-019',
    message: 'SR connection could not be saved. Please try again.',
  },
};

export function oauthCallbackToSrError(srError: string): SRErrorDisplay {
  return (
    SR_OAUTH_CALLBACK_ERRORS[srError] ?? {
      code: 'SR-010',
      message: 'SR connection could not be completed. Please try again.',
    }
  );
}

export function toSrErrorDisplay(
  err: unknown,
  fallback: SRErrorDisplay,
): SRErrorDisplay {
  if (err instanceof SRApiError) {
    return { code: err.code, message: err.message };
  }
  return fallback;
}

type SrErrorContext =
  | 'connection'
  | 'config_status'
  | 'oauth_start'
  | 'disconnect'
  | 'hub_login'
  | 'referrals'
  | 'summary'
  | 'ingest_failures'
  | 'ingest_retry'
  | 'queue_manual_triage'
  | 'validation_health'
  | 'message'
  | 'retry_outbound'
  | 'notifications'
  | 'notifications_ack';

const STATUS_DEFAULTS: Record<
  SrErrorContext,
  Record<number, SRErrorDisplay> & { default: SRErrorDisplay; network: SRErrorDisplay }
> = {
  connection: {
    401: { code: 'SR-002', message: 'Please sign in again to view SR connection status.' },
    403: { code: 'SR-003', message: 'You do not have permission to manage SR integration.' },
    default: { code: 'SR-004', message: 'Unable to load SR connection status. Please try again.' },
    network: { code: 'SR-001', message: 'Unable to reach the server. Check your connection and try again.' },
  },
  config_status: {
    401: { code: 'SR-002', message: 'Please sign in again to view SR configuration status.' },
    403: { code: 'SR-003', message: 'You do not have permission to view SR configuration.' },
    default: { code: 'SR-004', message: 'Unable to load SR configuration status. Please try again.' },
    network: { code: 'SR-001', message: 'Unable to reach the server. Check your connection and try again.' },
  },
  oauth_start: {
    403: { code: 'SR-006', message: 'Admin access is required to connect SR integration.' },
    default: { code: 'SR-005', message: 'Unable to start SR connection. Please try again.' },
    network: { code: 'SR-005', message: 'Unable to start SR connection. Please try again.' },
  },
  disconnect: {
    default: { code: 'SR-011', message: 'Unable to disconnect from SR. Please try again.' },
    network: { code: 'SR-011', message: 'Unable to disconnect from SR. Please try again.' },
  },
  hub_login: {
    400: { code: 'SR-012', message: 'Connect SR integration before launching the portal.' },
    401: { code: 'SR-013', message: 'SR session expired. Reconnect and try again.' },
    502: { code: 'SR-014', message: 'SR portal is temporarily unavailable. Please try again.' },
    default: { code: 'SR-015', message: 'Unable to open SR portal. Please try again.' },
    network: { code: 'SR-015', message: 'Unable to open SR portal. Please try again.' },
  },
  referrals: {
    401: { code: 'SR-002', message: 'Please sign in again to view SR referrals.' },
    403: { code: 'SR-021', message: 'Admin access is required to view SR referrals.' },
    default: { code: 'SR-022', message: 'Unable to load SR referrals. Please try again.' },
    network: { code: 'SR-020', message: 'Unable to load SR referrals. Please try again.' },
  },
  summary: {
    401: { code: 'SR-002', message: 'Please sign in again to view SR queue summary.' },
    403: { code: 'SR-031', message: 'Admin access is required to view SR queue summary.' },
    default: { code: 'SR-032', message: 'Unable to load SR queue summary.' },
    network: { code: 'SR-030', message: 'Unable to load SR queue summary.' },
  },
  ingest_failures: {
    401: { code: 'SR-002', message: 'Please sign in again to view failed ingestions.' },
    403: { code: 'SR-051', message: 'Admin access is required to view failed ingestions.' },
    default: { code: 'SR-052', message: 'Unable to load failed ingestions. Please try again.' },
    network: { code: 'SR-050', message: 'Unable to load failed ingestions. Please try again.' },
  },
  ingest_retry: {
    401: { code: 'SR-002', message: 'Please sign in again to retry ingest.' },
    403: { code: 'SR-051', message: 'Admin access is required to retry ingest.' },
    404: { code: 'SR-054', message: 'No retryable ingest found for this post.' },
    409: { code: 'SR-054', message: 'Ingest is still processing — retry again later.' },
    502: { code: 'SR-054', message: 'Ingest retry failed. Please try again.' },
    default: { code: 'SR-054', message: 'Unable to retry ingest. Please try again.' },
    network: { code: 'SR-054', message: 'Unable to retry ingest. Please try again.' },
  },
  queue_manual_triage: {
    401: { code: 'SR-002', message: 'Please sign in again to queue manual triage.' },
    403: { code: 'SR-051', message: 'Admin or manager access is required to queue manual triage.' },
    409: {
      code: 'SR-053',
      message: 'This referral is not in triage-failed state and cannot be queued.',
    },
    default: { code: 'SR-053', message: 'Unable to queue referral for manual triage. Please try again.' },
    network: { code: 'SR-053', message: 'Unable to queue referral for manual triage. Please try again.' },
  },
  validation_health: {
    401: { code: 'SR-002', message: 'Please sign in again to view validation health.' },
    403: { code: 'SR-051', message: 'Admin access is required to view validation health.' },
    default: { code: 'SR-055', message: 'Unable to load validation health. Please try again.' },
    network: { code: 'SR-055', message: 'Unable to load validation health. Please try again.' },
  },
  message: {
    default: { code: 'SR-040', message: 'Unable to send SR message. Please try again.' },
    network: { code: 'SR-040', message: 'Unable to send SR message. Please try again.' },
  },
  retry_outbound: {
    default: { code: 'SR-041', message: 'Unable to retry SR notification. Please try again.' },
    network: { code: 'SR-041', message: 'Unable to retry SR notification. Please try again.' },
  },
  notifications: {
    401: { code: 'SR-002', message: 'Please sign in again to view SR notifications.' },
    403: { code: 'SR-060', message: 'You do not have permission to view SR notifications.' },
    default: { code: 'SR-061', message: 'Unable to load SR notifications. Please try again.' },
    network: { code: 'SR-060', message: 'Unable to load SR notifications. Please try again.' },
  },
  notifications_ack: {
    401: { code: 'SR-002', message: 'Please sign in again to acknowledge this notification.' },
    403: { code: 'SR-062', message: 'You do not have permission to acknowledge this notification.' },
    default: { code: 'SR-063', message: 'Unable to acknowledge notification. Please try again.' },
    network: { code: 'SR-063', message: 'Unable to acknowledge notification. Please try again.' },
  },
};

function mapBackendBodyToCode(
  context: SrErrorContext,
  backendError: string,
): SRErrorDisplay | null {
  const normalized = backendError.toLowerCase();

  if (context === 'hub_login') {
    if (normalized.includes('not active') || normalized.includes('configure sr integration')) {
      return STATUS_DEFAULTS.hub_login[400];
    }
    if (normalized.includes('session expired') || normalized.includes('reconnect')) {
      return STATUS_DEFAULTS.hub_login[401];
    }
    if (normalized.includes('did not return') || normalized.includes('failed to generate')) {
      return STATUS_DEFAULTS.hub_login[502];
    }
  }

  if (normalized.includes('admin') || normalized.includes('forbidden')) {
    if (context === 'connection') return STATUS_DEFAULTS.connection[403];
    if (context === 'oauth_start') return STATUS_DEFAULTS.oauth_start[403];
    if (context === 'referrals') return STATUS_DEFAULTS.referrals[403];
    if (context === 'summary') return STATUS_DEFAULTS.summary[403];
    if (context === 'ingest_failures') return STATUS_DEFAULTS.ingest_failures[403];
    if (context === 'ingest_retry') return STATUS_DEFAULTS.ingest_retry[403];
    if (context === 'queue_manual_triage') return STATUS_DEFAULTS.queue_manual_triage[403];
  }

  return null;
}

export async function srErrorFromResponse(
  response: Response,
  context: SrErrorContext,
): Promise<SRApiError> {
  const config = STATUS_DEFAULTS[context];
  const byStatus = config[response.status];
  if (byStatus) {
    return new SRApiError(byStatus.code, byStatus.message);
  }

  const body = await response.json().catch(() => ({}));
  const backendError = (body as { error?: string }).error ?? '';
  const mapped = backendError ? mapBackendBodyToCode(context, backendError) : null;
  if (mapped) {
    return new SRApiError(mapped.code, mapped.message);
  }

  const fallback = config.default;
  return new SRApiError(fallback.code, fallback.message);
}

export function srErrorFromNetwork(context: SrErrorContext): SRApiError {
  const { code, message } = STATUS_DEFAULTS[context].network;
  return new SRApiError(code, message);
}

export async function srFetch<T>(
  context: SrErrorContext,
  fetcher: () => Promise<Response>,
  parser: (response: Response) => Promise<T>,
): Promise<T> {
  let response: Response;
  try {
    response = await fetcher();
  } catch {
    throw srErrorFromNetwork(context);
  }

  if (!response.ok) {
    throw await srErrorFromResponse(response, context);
  }

  return parser(response);
}
